import { balbrigganCoords, blanchCoords, kerryCoords, moorockCoords, oranmoreCoords, pecanSquareCoords, pecanSquareTestSiteCoords } from '../constants';
import { createSelector } from 'reselect';
import { getDistanceBetweenPoints } from '../utils/parsingUtils';
import { RootState } from '../store/configureStore';
import { LandingPad } from '@m4a3/api-client';

export const selectLiveAircraftData = (state: RootState) => state.liveAircraft.liveData;
export const selectActivePads = (state: RootState): LandingPad[] => state.liveAircraft.activePads;
export const selectInactivePads = (state: RootState): LandingPad[] => state.liveAircraft.inactivePads;
export const selectPads = (state: RootState): LandingPad[] => state.liveAircraft.pads;
export const selectPadConfig = state => state.liveAircraft.padConfig;
export const selectShowSitl = (state: RootState) => state.liveAircraft.showSitl;

// Define local base selectors to avoid circular dependency with orders selectors
const selectCells = (state: RootState) => state.orders.cells;
const selectSelectedCellId = (state: RootState) => state.orders.selectedCellId;

export const selectCraftData = (state: RootState) =>
  Object.values(state.liveAircraft.liveData || {});
export const selectLocation = state => {
  return state.liveAircraft.location;
};
export const selectAircraft = state => {
  return state.liveAircraft.chosenAircraft;
};
export const selectClients = (state: RootState) => {
  return state.liveAircraft.clients;
};

export const selectLocations = createSelector(selectCraftData, craftData => {
  try {
    const data = craftData.map(craft => (craft as any).location?.name);

    return [...new Set(data)];
  } catch (err) {
    return [];
  }
});
export const selectActiveCellPads = createSelector(
  selectActivePads,
  selectSelectedCellId,
  (pads, cellId) => {
    try {
      return pads.filter(pad => pad.cellId === cellId);
    } catch (err) {
      return [];
    }
  }
);
export const selectCellPads = createSelector(
  selectPads,
  selectSelectedCellId,
  (pads, cellId) => {
    try {
      return pads.filter(pad => pad.cellId === cellId);
    } catch (err) {
      return [];
    }
  }
);

export const getActiveCraft = createSelector(
  selectSelectedCellId,
  selectCells,
  selectLiveAircraftData,
  (selectedCellId, cells, liveData) => {
    try {
      const defaultCoords = blanchCoords;
      const matchingCell = cells.find(({id}) => id === selectedCellId);
      const fortyKmInMetres = 40000;
      const coords = matchingCell ? [matchingCell.centerLatitude, matchingCell.centerLongitude] : defaultCoords;

     const uavNames = Object.values(liveData)
        .map(liveData => {
          const { currentPosition } = liveData as any;
          const dist = getDistanceBetweenPoints(
            coords[0],
            coords[1],
            currentPosition[0],
            currentPosition[1]
          );

          if (dist < fortyKmInMetres) {
            return (liveData as any)?.uavId;
          }
          return null;
        })
        .filter(Boolean);

      return uavNames;
    } catch (err) {
      return [];
    }
  }
);

export const selectCellClients = createSelector(
  selectSelectedCellId,
  selectCells,
  selectClients,
  (selectedCellId, cells, clients) => {
    try {
      const matchingCell = cells.find(({ id }) => id === selectedCellId);
      if (!matchingCell) return clients;

      const coords = [matchingCell.centerLatitude, matchingCell.centerLongitude];
      const fortyKmInMetres = 40000;

      return clients.filter(client => {
        if (!client?.position?.latitude || !client?.position?.longitude) return true;
        const dist = getDistanceBetweenPoints(
          coords[0],
          coords[1],
          client.position.latitude,
          client.position.longitude
        );
        return dist < fortyKmInMetres;
      });
    } catch (err) {
      return clients;
    }
  }
);

export const selectLandingZoneId = (_: RootState, landingZoneId: string) => landingZoneId;
export const selectPadsByLandingZoneId = createSelector(
  selectPads,
  selectLandingZoneId,
  (pads, landingZoneId) => {
    return pads.filter(pad => pad.landingZoneId === landingZoneId);
  }
);